import { useState, useEffect, useRef } from "react";
import "../asset/css/Home.css";
import Navbar from "../component/Navbar";
import Hero from "../component/Hero";
import Footer from "../component/Footer";

/* ── data ─────────────────────────────────────── */
const GENRES = [
  { emoji: "🎸", title: "Rock & Metal",        desc: "Distortion, power, and raw energy. Guitar gods welcome.",            entries: "3,200", clr: "#FF6B35" },
  { emoji: "🎹", title: "Electronic & EDM",    desc: "Drops, synths, and basslines that shake the floor.",                entries: "5,800", clr: "#8338EC" },
  { emoji: "🎺", title: "Jazz & Blues",         desc: "Soulful improvisation and timeless groove.",                        entries: "1,900", clr: "#FFB703" },
  { emoji: "🎵", title: "Hip-Hop & R&B",        desc: "Bars, beats, and stories straight from the streets.",              entries: "7,400", clr: "#06D6A0" },
  { emoji: "🎻", title: "Classical & Orchestral", desc: "Timeless compositions meeting modern audiences.",               entries: "1,200", clr: "#E74C3C" },
  { emoji: "🎙️", title: "Pop & Indie",          desc: "Catchy hooks, emotional lyrics, radio-ready anthems.",            entries: "9,100", clr: "#3498DB" },
  { emoji: "🥁", title: "World & Folk",          desc: "Roots music from every corner of the globe.",                    entries: "2,400", clr: "#E67E22" },
  { emoji: "🎤", title: "Vocal Performance",    desc: "Pure voice, pure emotion, pure power.",                            entries: "4,600", clr: "#2ECC71" },
];

const STEPS = [
  {
    num: "01",
    img: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80",
    title: "Create Your Account",
    desc: "Sign up for free in under 60 seconds. Fill in your artist profile, add a bio, link your social media, and choose your genre categories. Your profile becomes your musical identity on the platform.",
    list: ["Free account forever", "Artist profile & portfolio", "Track upload history", "Judge feedback inbox"],
    cta: "Create Account",
  },
  {
    num: "02",
    img: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&q=80",
    title: "Upload Your Track",
    desc: "Record your best work, then upload it directly from our dashboard. We accept MP3, WAV, FLAC and AIFF up to 320kbps. Add a title, cover art, description, and pick the right category.",
    list: ["MP3 / WAV / FLAC support", "Cover art upload", "Public or private submission", "Revision window of 48 hrs"],
    cta: "Upload Music",
  },
  {
    num: "03",
    img: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&q=80",
    title: "Get Judged & Win",
    desc: "Industry-certified judges anonymously score every submission on melody, production, originality and emotional impact. Top-scoring tracks advance to the finals — broadcast live worldwide.",
    list: ["Anonymous scoring panel", "Multi-criteria evaluation", "Live-streamed finals", "Results within 14 days"],
    cta: "See How Judging Works",
  },
];

const PRIZES = [
  {
    tier: "gold",   icon: "🥇", place: "1st Place",  amount: "$25,000",
    perks: ["Cash Prize", "Record Deal Introduction", "Music Video Production", "World Tour Opportunity", "Platinum Mentorship"],
  },
  {
    tier: "silver", icon: "🥈", place: "2nd Place", amount: "$15,000",
    perks: ["Cash Prize", "Studio Recording Session", "Label Introductions", "Gold Mentorship Package"],
  },
  {
    tier: "bronze", icon: "🥉", place: "3rd Place", amount: "$10,000",
    perks: ["Cash Prize", "Online Promotion Package", "Music Distribution Deal", "Silver Mentorship"],
  },
];

const EXTRAS = [
  { icon: "🎯", title: "Best Production",   desc: "Gear bundle worth $3,000" },
  { icon: "🌍", title: "Audience Choice",   desc: "Global fan vote winner" },
  { icon: "📱", title: "Social Buzz Award", desc: "Viral marketing campaign" },
  { icon: "🎓", title: "Rookie of the Year", desc: "Full scholarship & mentorship" },
];

const JUDGES = [
  {
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80",
    name: "Marcus Leon",
    role: "Platinum Producer",
    genre: "Electronic",
    bio: "Grammy-nominated producer behind 14 top-10 hits. Former head of A&R at Universal Music Group.",
  },
  {
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80",
    name: "Aria Voss",
    role: "Classical Conductor",
    genre: "Classical",
    bio: "Principal conductor for the Berlin Philharmonic youth ensemble. Passionate advocate for new composers.",
  },
  {
    img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80",
    name: "DJ Krave",
    role: "Hip-Hop Legend",
    genre: "Hip-Hop",
    bio: "Pioneering DJ and beatmaker. Credited with shaping the sound of East Coast hip-hop for two decades.",
  },
  {
    img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80",
    name: "Sofia Reyes",
    role: "Pop Songwriter",
    genre: "Pop",
    bio: "Billboard Hot 100 songwriter with over 2B streams. Mentored emerging artists across 6 continents.",
  },
];

const TESTIMONIALS = [
  {
    avatar: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=100&q=80",
    name: "Arjun Mehta",
    from: "Mumbai, India · EDM Producer",
    text: "Entering SoundBattle was the best decision of my music career. The judges gave feedback that transformed my production style, and placing 2nd brought me a management deal I'm still benefiting from today.",
  },
  {
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&q=80",
    name: "Chloé Dupont",
    from: "Paris, France · Singer-Songwriter",
    text: "I was a bedroom artist with zero industry connections. Three months after winning the Vocal Performance category, I had a record deal and was touring across Europe. SoundBattle is absolutely life-changing.",
  },
  {
    avatar: "https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=100&q=80",
    name: "Kwame Asante",
    from: "Accra, Ghana · Afrobeats Artist",
    text: "The platform respects every genre and every culture. Winning the Audience Choice award opened doors I never imagined. The organizers genuinely care about artists from all over the world.",
  },
];

const TIMELINE = [
  { date: "Mar 1 – Apr 30, 2025", title: "Submissions Open",   desc: "Upload your track and complete your profile. All genres accepted. Multiple entries allowed in different categories.", badge: "live", badgeText: "LIVE NOW" },
  { date: "May 1 – May 15, 2025", title: "Preliminary Judging", desc: "Expert panel scores all submissions anonymously. Top 10% of entries per genre advance to the semi-finals.", badge: "upcoming", badgeText: "UPCOMING" },
  { date: "May 20 – Jun 5, 2025", title: "Semi-Finals",         desc: "Finalists revealed. Live community voting begins alongside the second round of judging.", badge: null, badgeText: "" },
  { date: "Jun 20, 2025",         title: "Grand Finals — Live", desc: "World-class broadcast event. Top 3 per genre perform/stream live. Judges deliberate on camera. Winners announced.", badge: null, badgeText: "" },
];

const FAQS = [
  { q: "Who can enter SoundBattle?", a: "Anyone 16+ worldwide. Whether you're an unsigned bedroom artist or a touring musician without a major label deal, you're eligible to compete." },
  { q: "How do I upload my music?", a: "After signing up, go to your dashboard and click 'Submit Entry'. You can upload MP3, WAV, FLAC or AIFF files up to 100MB. We support cover art uploads too." },
  { q: "How does the judging work?", a: "All submissions are judged anonymously — judges never see your name or profile. Tracks are scored on melody, production quality, originality, and emotional impact." },
  { q: "Can I enter multiple categories?", a: "Yes! You may submit up to two tracks in different genre categories per season. Each entry requires a separate submission." },
  { q: "How are prizes paid out?", a: "Prizes are distributed via bank transfer, PayPal, or cryptocurrency within 30 days of the finals. Tax requirements vary by country." },
  { q: "I'm a judge. How do I log in?", a: "Judges receive a dedicated login link via their registered email. Use the 'Judge Login' button on the nav and enter your credentials. Contact support if you haven't received access." },
];

/* ── helpers ───────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function useCountUp(ref, target) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      let start = 0;
      const step = Math.ceil(target / 60);
      const tick = () => {
        start = Math.min(start + step, target);
        el.textContent = start.toLocaleString();
        if (start < target) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, target]);
}

/* ── stat counter ──────────────────────────────── */
function CountUp({ target, suffix = "" }) {
  const ref = useRef(null);
  useCountUp(ref, target);
  return <><span ref={ref}>0</span>{suffix}</>;
}

/* ── waveform bars ─────────────────────────────── */
const HEIGHTS = [12,22,32,18,28,38,20,14,30,24,36,16,26,34,10,28,20,38,14,24,32,18,28,12,36,22,30,16,26,38];
function Waveform() {
  return (
    <div className="waveform-wrap" aria-hidden>
      {HEIGHTS.map((h, i) => (
        <span key={i} style={{ height: h, animationDelay: `${(i * 0.07) % 1}s` }} />
      ))}
    </div>
  );
}

/* ── main component ────────────────────────────── */
const Home = () => {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [openFaq,  setOpenFaq]    = useState(null);
  const cursorRef     = useRef(null);
  const cursorRingRef = useRef(null);

  useReveal();

  /* navbar scroll */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* custom cursor */
  useEffect(() => {
    const move = (e) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = e.clientX + "px";
        cursorRef.current.style.top  = e.clientY + "px";
      }
      if (cursorRingRef.current) {
        cursorRingRef.current.style.left = e.clientX + "px";
        cursorRingRef.current.style.top  = e.clientY + "px";
      }
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  /* lock body when menu open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const navLinks = ["about", "genres", "how-it-works", "prizes", "judges", "timeline", "faq"];

  return (
    <>
      {/* Cursor */}
      <div className="cursor"      ref={cursorRef}     aria-hidden />
      <div className="cursor-ring" ref={cursorRingRef} aria-hidden />

      {/* ── NAVBAR ── */}
       <Navbar /> 


       <Hero />


      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      {/* <section className="hero" id="hero">
        <div className="hero-bg" aria-hidden />
        <div className="hero-grid" aria-hidden />

        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot" />
            Season 4 Now Open — $50,000 Prize Pool
          </div>

          <h1 className="hero-title">
            WHERE<br />
            <span className="title-stroke">MUSIC</span><br />
            <span className="title-accent">MEETS GLORY</span>
          </h1>

          <p className="hero-sub">
            Upload your track. Face the world's best. Win legendary prizes.
            The most electrifying online music competition — for every genre, every artist.
          </p>

          <div className="hero-actions">
            <a href="#signup" className="btn-hero-primary">
              Enter the Battle
              <svg viewBox="0 0 24 24" fill="none" width={18}>
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a href="#how-it-works" className="btn-hero-ghost">
              <span className="play-circle">▶</span>
              How It Works
            </a>
          </div>

          <div className="hero-stats">
            {[
              { target: 48000, suffix: "+", label: "Artists Competed" },
              { target: 120,   suffix: "+", label: "Countries" },
              { target: 50,    suffix: "K", label: "Prize Pool ($)" },
              { target: 200,   suffix: "+", label: "Celeb Judges" },
            ].map((s, i) => (
              <div key={i} style={{ display: "contents" }}>
                {i > 0 && <div className="stat-divider" aria-hidden />}
                <div className="stat-item">
                  <span className="stat-num"><CountUp target={s.target} suffix={s.suffix} /></span>
                  <span className="stat-label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

         
        <div className="hero-visual">
          <div className="vinyl">
            <div className="vinyl-glow" aria-hidden />
            <img
              src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600&q=80"
              alt="Vinyl record"
              className="vinyl-img"
            />
            <div className="vinyl-overlay" aria-hidden />
            <div className="vinyl-center">♫</div>
          </div>
        </div>

        <Waveform />

        <div className="scroll-cue">
          <span>Scroll</span>
          <div className="scroll-line" aria-hidden />
        </div>
      </section> */}

     
      <div className="ticker-wrap" aria-hidden>
        <div className="ticker">
          {Array(3).fill(null).map((_, i) =>
            ["🎵 Season 4 Open","🏆 $50,000 Prize Pool","🎤 All Genres Welcome","🎧 Pro Judges","🌍 Global Stage"].map((t) => (
              <span key={`${i}-${t}`}>{t}</span>
            ))
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          ABOUT
      ══════════════════════════════════════════ */}
      <section className="about" id="about">
        <div className="container">
          <div className="section-tag reveal">About SoundBattle</div>
          <div className="about-grid">
            <div className="about-text">
              <h2 className="section-title reveal reveal-delay-1">
                The World's Most <span className="highlight">Competitive</span> Music Stage
              </h2>
              <p className="reveal reveal-delay-2">
                SoundBattle is the premier online music competition platform where emerging and established
                artists submit their best tracks to be judged by industry professionals, celebrated producers,
                and legendary musicians from around the world.
              </p>
              <p className="reveal reveal-delay-2">
                We believe talent knows no borders. Whether you're a bedroom producer in Mumbai, a jazz
                saxophonist in New Orleans, or a classical violinist in Vienna — your music deserves a
                global stage. SoundBattle is that stage.
              </p>
              <p className="reveal reveal-delay-3">
                With multiple genre categories, real cash prizes, record label exposure, and mentorship from
                world-class judges, there has never been a better time or place to prove yourself.
              </p>
              <div className="about-features reveal reveal-delay-3">
                {[
                  ["🎯","Genre-specific categories"],
                  ["🔒","Anonymous judging — talent only"],
                  ["📡","Live feedback from judges"],
                  ["🎙️","Record label partnerships"],
                ].map(([icon, text]) => (
                  <div className="feat" key={text}>
                    <span className="feat-icon">{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="about-visual reveal reveal-delay-2">
              <img
                src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=700&q=80"
                alt="Live music stage"
                className="about-img"
              />
              <div className="about-badge">
                <strong>$50K</strong>
                <span>PRIZE POOL</span>
              </div>
              <div className="about-badge2">
                <strong>S4</strong>
                <span>NOW LIVE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          GENRES
      ══════════════════════════════════════════ */}
      <section className="genres" id="genres">
        <div className="container">
          <div className="section-tag reveal">Competition Categories</div>
          <h2 className="section-title reveal reveal-delay-1">
            Every Genre. <span className="highlight-gold">One Stage.</span>
          </h2>
          <p className="section-sub reveal reveal-delay-2">
            Compete in the category that defines you — or surprise us all.
          </p>
          <div className="genres-grid">
            {GENRES.map((g, i) => (
              <div
                className={`genre-card reveal reveal-delay-${(i % 4) + 1}`}
                key={g.title}
                style={{ "--gc": g.clr }}
              >
                <div className="genre-emoji">{g.emoji}</div>
                <h3>{g.title}</h3>
                <p>{g.desc}</p>
                <span className="genre-entries">{g.entries} entries this season</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════ */}
      <section className="how-section" id="how-it-works">
        <div className="container">
          <div className="section-tag reveal">Process</div>
          <h2 className="section-title dark reveal reveal-delay-1">
            How <span className="highlight">It Works</span>
          </h2>
          <p className="section-sub dark reveal reveal-delay-2">
            Three simple steps stand between you and the spotlight.
          </p>
          <div className="steps-grid">
            {STEPS.map((s, i) => (
              <div className={`step-card reveal reveal-delay-${i + 1}`} key={s.num}>
                <div className="step-img-wrap">
                  <img src={s.img} alt={s.title} loading="lazy" />
                  <div className="step-num-badge">{s.num}</div>
                </div>
                <div className="step-body">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <ul className="step-list">
                    {s.list.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                  <a href="#signup" className="step-cta">{s.cta} →</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          PRIZES
      ══════════════════════════════════════════ */}
      <section className="prizes-section" id="prizes">
        <div className="prizes-bg-glow" aria-hidden />
        <div className="container">
          <div className="section-tag reveal">Prize Structure</div>
          <h2 className="section-title reveal reveal-delay-1">
            Win <span className="highlight-gold">Legendary</span> Prizes
          </h2>
          <p className="section-sub reveal reveal-delay-2">
            Season 4 boasts the biggest prize pool in SoundBattle history.
          </p>

          <div className="prizes-grid">
            {PRIZES.map((p, i) => (
              <div className={`prize-card ${p.tier} reveal reveal-delay-${i + 1}`} key={p.place}>
                <div className="prize-icon">{p.icon}</div>
                <div className="prize-place">{p.place}</div>
                <div className="prize-amount">{p.amount}</div>
                <ul className="prize-perks">
                  {p.perks.map((perk) => <li key={perk}>✦ {perk}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <div className="prizes-extras">
            {EXTRAS.map((e, i) => (
              <div className={`extra-prize reveal reveal-delay-${i + 1}`} key={e.title}>
                <div className="extra-icon">{e.icon}</div>
                <div>
                  <div className="extra-title">{e.title}</div>
                  <div className="extra-desc">{e.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          JUDGES
      ══════════════════════════════════════════ */}
      <section className="judges-section" id="judges">
        <div className="container">
          <div className="section-tag reveal">Meet the Panel</div>
          <h2 className="section-title reveal reveal-delay-1">
            World-Class <span className="highlight">Judges</span>
          </h2>
          <p className="section-sub reveal reveal-delay-2">
            Your music will be heard — and evaluated — by the industry's finest.
          </p>
          <div className="judges-grid">
            {JUDGES.map((j, i) => (
              <div className={`judge-card reveal reveal-delay-${i + 1}`} key={j.name}>
                <div className="judge-img-wrap">
                  <img src={j.img} alt={j.name} loading="lazy" />
                  <div className="judge-overlay" aria-hidden />
                  <span className="judge-genre-tag">{j.genre}</span>
                </div>
                <div className="judge-info">
                  <h3>{j.name}</h3>
                  <div className="judge-role">{j.role}</div>
                  <p className="judge-bio">{j.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════ */}
      <section className="testimonials-section" id="testimonials">
        <div className="container">
          <div className="section-tag reveal">Artist Stories</div>
          <h2 className="section-title reveal reveal-delay-1">
            Lives <span className="highlight-green">Changed</span>
          </h2>
          <p className="section-sub reveal reveal-delay-2">
            Don't take our word for it — hear from artists whose careers we've helped launch.
          </p>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div className={`testimonial-card reveal reveal-delay-${i + 1}`} key={t.name}>
                <div className="stars">★★★★★</div>
                <div className="testimonial-quote">"</div>
                <p className="testimonial-text">{t.text}</p>
                <div className="testimonial-author">
                  <img src={t.avatar} alt={t.name} className="testimonial-avatar" loading="lazy" />
                  <div>
                    <span className="testimonial-name">{t.name}</span>
                    <span className="testimonial-from">{t.from}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          TIMELINE
      ══════════════════════════════════════════ */}
      <section className="timeline-section" id="timeline">
        <div className="container">
          <div className="section-tag reveal">Season Schedule</div>
          <h2 className="section-title dark reveal reveal-delay-1">
            Competition <span className="highlight">Timeline</span>
          </h2>
          <p className="section-sub dark reveal reveal-delay-2">
            Key dates for Season 4 — mark your calendar and don't miss a beat.
          </p>
          <div className="timeline">
            {TIMELINE.map((item, i) => (
              <div className={`timeline-item reveal reveal-delay-${i + 1}`} key={item.title}>
                <div className="timeline-dot" aria-hidden />
                <div className="timeline-date">{item.date}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                {item.badge && (
                  <span className={`timeline-badge ${item.badge}`}>{item.badgeText}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ══════════════════════════════════════════
          CTA
      ══════════════════════════════════════════ */}
      <section className="cta-section" id="signup">
        <h2>READY TO COMPETE?</h2>
        <p>Join 48,000 artists. Prove your talent. Claim your glory.</p>
        <div className="cta-btn-wrap">
          <a href="#signup" className="btn-cta-white">Sign Up Free — It's Your Time</a>
          <a href="#login"  className="btn-cta-dark">Judge Login →</a>
        </div>
      </section>

      
      {/* ══════════════════════════════════════════
          FAQ
      ══════════════════════════════════════════ */}
      {/* <section className="faq-section" id="faq">
        <div className="container">
          <div className="section-tag reveal">FAQ</div>
          <h2 className="section-title reveal reveal-delay-1">
            Got <span className="highlight">Questions?</span>
          </h2>
          <p className="section-sub reveal reveal-delay-2">
            Everything you need to know before you hit upload.
          </p>
          <div className="faq-list">
            {FAQS.map((f, i) => (
              <div
                className={`faq-item reveal ${openFaq === i ? "open" : ""}`}
                key={f.q}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  {f.q}
                  <span className="faq-icon" aria-hidden>+</span>
                </button>
                <div className="faq-answer" role="region">
                  <p>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}


<section className="fq-section" id="faq">
 
  {/* background decoration */}
  <div className="fq-bg-accent" aria-hidden />
 
  <div className="fq-container">
 
    {/* ── left panel ── */}
    <div className="fq-left">
      <span className="fq-tag">FAQ</span>
      <h2 className="fq-title">
        Got<br />
        <span className="fq-title-red">Questions?</span>
      </h2>
      <p className="fq-desc">
        Everything you need to know before hitting upload. Can't find your answer?
        Our support team responds within 2 hours.
      </p>
      <a href="mailto:support@soundbattle.io" className="fq-contact-btn">
        <span className="fq-contact-icon">✉</span>
        Contact Support
      </a>
 
      {/* decorative note card */}
      <div className="fq-note-card">
        <div className="fq-note-top">
          <span className="fq-note-dot red" />
          <span className="fq-note-dot yellow" />
          <span className="fq-note-dot green" />
        </div>
        <div className="fq-note-lines">
          <span />
          <span style={{ width: "70%" }} />
          <span style={{ width: "85%" }} />
          <span style={{ width: "55%" }} />
          <span style={{ width: "90%" }} />
        </div>
        <div className="fq-note-label">rules &amp; guidelines.pdf</div>
      </div>
    </div>
 
    {/* ── right accordion ── */}
    <div className="fq-right">
      {FAQS.map((f, i) => (
        <div
          className={`fq-item ${openFaq === i ? "fq-open" : ""}`}
          key={f.q}
        >
          <button
            className="fq-question"
            onClick={() => setOpenFaq(openFaq === i ? null : i)}
            aria-expanded={openFaq === i}
          >
            <span className="fq-q-num">0{i + 1}</span>
            <span className="fq-q-text">{f.q}</span>
            <span className="fq-chevron" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" width="18">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
 
          <div className="fq-answer-wrap" role="region">
            <div className="fq-answer">
              <p>{f.a}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
 
  </div>
</section>



      {/* ══════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════ */}
     <Footer />


    </>
  );
};

export default Home;